import { type QueryClient } from '@tanstack/query-core';
import { log, logExceptionOnce } from '@hungryroot/o11y';
import {
  getAnonymousId,
  trackExperimentEnrollment,
  type EnrollmentMethod,
} from '@hungryroot/segment';
import { withTimeoutOrNull } from '@hungryroot/segment/with-timeout';
import {
  findExperimentDefinition,
  getWeightedVariants,
  rollHoldout,
  selectRandom,
  toStoredId,
  type ForceVariantEnv,
} from '../enrollment-shared';
import {
  ExperimentDefinitions,
  type ExperimentVariants,
  type Experiments,
  type ForceVariantFn,
} from '../experiments';
import {
  ExperimentDefinitionsApiError,
  experimentDefinitionsKey,
  fetchExperimentDefinitions,
} from '../hooks/useExperimentDefinitions';
import { getPersistedEnrollment, persistedEnrollment } from './persist';

/**
 * How long enrollment waits for Segment's anonymous id before logging the
 * enrollment without it. `getAnonymousId()` sits on analytics-next's pre-init
 * buffer, which only flushes once the SDK has fetched its settings — when that
 * fetch is blocked (tracker blockers, strict browser privacy modes) the promise
 * never settles, so an unbounded await here would stall enrollment, and every
 * surface that gates render on `isLoading`, indefinitely. Mirrors the bound in
 * `salad-shooter/getStreamId.ts`.
 */
const ANONYMOUS_ID_TIMEOUT_MS = 2_000;

/**
 * Segment's anonymous id for the enrollment log, or `null` if Segment is
 * blocked, disabled, or slow to initialize. The id is a join key for analytics
 * only — enrollment must never wait on it.
 */
async function getAnonymousIdForLog(): Promise<string | null> {
  const anonymousId = await withTimeoutOrNull(
    getAnonymousId(),
    ANONYMOUS_ID_TIMEOUT_MS,
    'Segment anonymous id'
  );
  return anonymousId ?? null;
}

function concurrent<T extends Experiments>(
  expId: T,
  child: () => Promise<ExperimentVariants<T> | null>
) {
  return async () => {
    return new Promise<ExperimentVariants<T> | null>((res) => {
      const queue = enrollmentQueue[expId];

      // Handle enrollment requests that are initiated
      // while we are asynchronously enrolling a user
      // We want to queue the resolve callback until
      // after the enrollment has happened ONCE
      if (queue !== undefined) {
        queue.push(res);
        return;
      }

      const pendingEnrollmentQueue = (enrollmentQueue[expId] = [res]);

      // A throw anywhere in `child` must still settle every queued caller —
      // an unsettled promise here leaves `useExperimentFlags` loading forever.
      void child()
        .catch((error: unknown) => {
          logExceptionOnce(error, {
            key: 'getExperimentEnrollment.enroll.error',
          });
          return null;
        })
        .then((result) => {
          flushQueue(expId, result, pendingEnrollmentQueue);
        });
    });
  };
}

type OnEnrollmentResolve<T extends Experiments> = (
  variant: ExperimentVariants<T> | null
) => void;

let enrollmentQueue: Partial<
  Record<Experiments, OnEnrollmentResolve<Experiments>[]>
> = {};

function flushQueue<T extends Experiments>(
  exp: T,
  variant: ExperimentVariants<T> | null,
  callbacks: OnEnrollmentResolve<Experiments>[]
) {
  callbacks.forEach((callback) => callback(variant));
  enrollmentQueue[exp] = undefined;
}

async function queryExperimentDefinitions(queryClient: QueryClient) {
  const queryParams = {
    limit: 1000,
  };

  return await queryClient.fetchQuery({
    queryKey: experimentDefinitionsKey(queryParams),
    queryFn: async () => {
      return fetchExperimentDefinitions(queryParams);
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
    gcTime: 15 * 60 * 1000,
  });
}

/**
 *
 * This function takes an experiment id (and other things!)
 * and resolves the user's enrollment state. It is designed to be called within a React Hook.
 * The React Hook should understand how to read and write cookies, access React Query query client.
 * The hook MUST pass down those functions here for consumption.
 *
 * This function MUST ALWAYS return synchronously. The return value is how downstream
 * callers will know which state the user is in and what actions they need to perform.
 *
 * The following are the user enrollment states.
 *
 *
 * OPTED OUT
 * The user is in an OPTED OUT state when there enrollment value is currently less than 0.
 * When a user is in this state, we return
 * the variant SYNCHRONOUSLY to allow downstream code to render as quickly as possible.
 *
 * ENROLLED
 * In this state, we have an experiment variant for the user in local cookies, but we have
 * to verify that the experiment itself has not been rev'd on the backend. In this state, we SYNCHRONOUSLY return
 * a tuple containing the variant stored in cookies alongside a function called "checkRevvedExperiment". "checkRevvedExperiment" is
 * called by downstream consumers. It emits a new variant if needed based on how the experiment has been rev'd
 *
 * NOT_ENROLLED
 * Is this state, the user has not been enrolled. We do not have a value in cookies. We have to go to
 * the server to figure out what variants are possible.
 *
 */

export function getExperimentEnrollment<T extends Experiments>({
  expId,
  queryClient,
  env,
}: {
  expId: T;
  queryClient: QueryClient;
  env: ForceVariantEnv;
}) {
  const cookieValue = getPersistedEnrollment(expId);

  function enroll(
    experimentId: number,
    variant: number,
    enrollmentMethod: EnrollmentMethod
  ) {
    // Log it to segment
    trackExperimentEnrollment({
      experiment: experimentId,
      variant,
      enrollmentMethod,
    });

    persistedEnrollment(experimentId, variant, enrollmentMethod);
  }

  // User has an experiment enrollment already
  // in cookies. Use that.
  if (cookieValue !== undefined) {
    const variant = cookieValue.variant as ExperimentVariants<T>;

    if (variant < 0) {
      return {
        state: 'opted-out',
        variant,
      } as const;
    }

    return {
      state: 'enrolled',
      variant,
    } as const;
  }

  return {
    state: 'not-enrolled',
    enroll: concurrent(expId, async function enrollExperimentAsync() {
      let def: ReturnType<typeof findExperimentDefinition<T>> = null;

      try {
        // Load Experiment Definitions from API
        const enrollmentData = await queryExperimentDefinitions(queryClient);

        // Pull the relevant experiment definitions from the server response
        def = findExperimentDefinition(expId, enrollmentData);
      } catch (error) {
        if (error instanceof ExperimentDefinitionsApiError) {
          logExceptionOnce(
            error,
            { key: 'getExperimentEnrollment.fetching.enrollment.data.error' },
            {
              responseData: error.data,
              responseStatus: error.status,
            }
          );
        }
      }

      // We can't find a definition. This may happen in code that
      // is trying to access the experiment before it's been added to the database
      // In this case, we cant resolve the variant (we don't know what they are)
      // So we emit null.
      // The result is _not_ persisted to cookies
      if (def === null) {
        return null;
      }

      const { serverDefinition, variants, controlWeight, holdoutWeight } = def;

      const { is_active: isActive, is_pre_signup: isPreSignup } =
        serverDefinition;

      if (isPreSignup === false) {
        // Somehow, a post signup experiment has made it here
        // This can happen if an anonymous user is able
        // to unintentionally access a page that should only
        // be visible to logged in users

        // Log that this happened then return null
        // Unclear what to do here.
        // The error is actually that an anonymous user got here
        // in the first place.
        log('experiments.enrollment.post-signup-experiment', {
          experiment: expId,
          serverDefinition,
        });

        return null;
      }

      const { length: variantsLength } = variants;
      const notAbleToDetermineVariant =
        isActive === false || variantsLength === 0;

      // We found an experiment definition, but it's set to inactive or there are no
      // variants defined.
      // In this case, we don't know how to enroll the user so we set the variant to 0
      // The result is _not_ persisted to cookies
      if (notAbleToDetermineVariant === true) {
        return null;
      }

      // `defineExperiments` uses `as const` inference, which strips the
      // optional `forceVariant` property from experiments that don't define
      // one, so we narrow the lookup through a minimal shape here.
      const localForceVariant = (
        ExperimentDefinitions[expId] as {
          forceVariant?: ForceVariantFn<readonly number[]>;
        }
      ).forceVariant;

      const weightedVariants = getWeightedVariants(variants, controlWeight);
      const forcedVariant = await localForceVariant?.(env);
      // Holdout is rolled only when no `forceVariant` override applies — the
      // forceVariant escape hatch exists specifically to exclude traffic from
      // measurement (e.g. embedded webviews) and must beat the holdout roll.
      const holdoutVariant =
        forcedVariant === undefined ? rollHoldout(holdoutWeight) : null;
      const enrollmentMethod: EnrollmentMethod =
        forcedVariant === undefined ? 'random' : 'forced';
      // The narrowed `ForceVariantFn<readonly number[]>` shape we read above
      // erases the per-experiment variants tuple, so TS widens `forcedVariant`
      // to `number`. The actual `ForceVariantFn<V>` declared on each
      // experiment can only return that experiment's variants or an
      // `ExcludeVariants` code, so the cast back to `ExperimentVariants<T>`
      // is sound.
      const variant = (
        forcedVariant !== undefined
          ? forcedVariant
          : holdoutVariant !== null
            ? holdoutVariant
            : selectRandom(weightedVariants)
      ) as ExperimentVariants<T>;

      // Log it to Datadog. Deliberately not awaited: the Segment anonymous id
      // is a join key for analytics, and resolving it must never delay
      // enrollment or the render that waits on it.
      void getAnonymousIdForLog().then((anonymousSegmentUserId) => {
        log('experiments.enrollment', {
          experiment: expId,
          // Segment anonymous user id so we can tie these to segment tables.
          // `null` when Segment is blocked, disabled, or slow to initialize.
          anonymousSegmentUserId,
          // stringifying these values so they show up
          // property in datadog
          variant: variant === null ? 'null' : variant,
          enrollmentMethod,
          serverDefinition:
            serverDefinition === undefined ? 'undefined' : serverDefinition,
        });
      });

      enroll(serverDefinition.id, variant, enrollmentMethod);

      return variant;
    }),
  } as const;
}

export function overrideExperimentVariant({
  overrides,
}: {
  overrides: Array<{
    expId: number;
    variant: number;
  }>;
}) {
  overrides.forEach(({ expId, variant }) => {
    persistedEnrollment(toStoredId(expId), variant, 'override');
  });
}

export function reset() {
  enrollmentQueue = {};
}
